import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, PenTool, Layers, Ban, Loader2, Eye, UploadCloud, FileImage } from "lucide-react";
import ImageLightbox from "./ImageLightbox";
import { ShareButtons } from "./ShareButtons";
import { toast } from "sonner";
import { api, fmt, API_BASE } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { lp, productAlt } from "../lib/localize";
import { useCart } from "../context/CartContext";
import { unitPrice, currentTier, currentPriceTier } from "../lib/pricing";
import { useLogoUpload } from "../lib/useLogoUpload";
import { lockScroll, unlockScroll } from "../lib/scrollLock";
import PersoTextarea from "./PersoTextarea";

// Popup produit : photo exacte, paliers, personnalisation (texte + logo), ajout panier
export default function ProductPopup({ productId, initialSizeId, initialFinishId, onClose }) {
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [sizeId, setSizeId] = useState(initialSizeId || null);
  const [finishId, setFinishId] = useState(initialFinishId || null);
  const [inscType, setInscType] = useState("aucune");
  const [inscText, setInscText] = useState("");
  const [color, setColor] = useState("");  // couleur choisie (variantes, ex. plaques d'identification)
  const [selImg, setSelImg] = useState(null);  // vignette de galerie choisie (ex. photo de groupe)
  const { add } = useCart();
  const { lang, t, ts } = useLang();
  const { logo, setLogo, uploading, fileInput, uploadLogo } = useLogoUpload(t);

  // taper un texte (ou ajouter un logo) avec « Sans inscription » → bascule vers le
  // type par défaut du produit (gravure pour les plaques, impression sinon)
  const onTextChange = (v) => {
    const maxLines = product?.max_lines || 0;  // ex. plaques d'identification : 4 lignes max
    if (maxLines > 0) {
      const lignes = v.split("\n");
      if (lignes.length > maxLines) v = lignes.slice(0, maxLines).join("\n");
    }
    setInscText(v);
    if (inscType === "aucune" && v.trim()) setInscType(product?.default_inscription || "impression");
  };

  useEffect(() => {
    setProduct(null);
    setQty(1);
    setInscType("aucune");
    setInscText("");
    api.get(`/products/${productId}`).then((res) => {
      setProduct(res.data);
      setSelImg(null);
      setInscType(res.data.default_inscription || "aucune");  // ex. plaques : gravure personnalisée présélectionnée
      const wanted = initialSizeId && res.data.sizes.find((s) => s.id === initialSizeId);
      setSizeId(wanted ? wanted.id : res.data.sizes[0].id);
      const fins = res.data.finishes || [];
      const wantedF = initialFinishId && fins.find((f) => f.id === initialFinishId);
      setFinishId(wantedF ? wantedF.id : fins[0]?.id || null);
      const cols = res.data.colors || [];
      setColor(cols[0] || "");  // couleur par défaut = 1re variante
    }).catch(() => { setProduct(false); onClose(); toast.error(t("product.load_error")); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Popup ouvert : gèle le défilement de la page (Lenis + CSS) — la molette
  // défile le contenu du popup (data-lenis-prevent), jamais le fond
  useEffect(() => {
    lockScroll();
    return () => unlockScroll();
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const unit = useMemo(
    () => (product ? unitPrice(product, sizeId, qty, inscType, finishId) : 0),
    [product, sizeId, finishId, qty, inscType]
  );

  // la photo affichée suit le fini sélectionné (image de la variante), sinon la
  // couleur choisie quand images[] suit l'ordre des couleurs (plaques d'identification)
  const curFinish = (product && product.finishes || []).find((f) => f.id === finishId);
  const colorList = product ? ((lang === "en" ? product.colors_en : product.colors) || []) : [];
  const colorIdx = colorList.indexOf(color);
  const colorImg = (colorList.length > 1 && product?.images?.length === colorList.length && colorIdx >= 0)
    ? product.images[colorIdx] : null;
  // Galerie : photo de l'item + photos supplémentaires (ex. photo de groupe des médailles)
  const gallery = product ? [...new Set([product.image, ...(product.images || [])].filter(Boolean))] : [];
  const displayImage = curFinish?.image || colorImg || selImg || product?.image;
  const [previewOpen, setPreviewOpen] = useState(false);

  // Échap ferme la fiche (accessibilité)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (product === false) return null;

  const INSCRIPTION_TYPES = [
    { id: "gravure", label: t("product.gravure"), icon: PenTool },
    // Impression couleur masquée pour certains produits (ex. médailles d'animaux : gravure seulement)
    ...(product?.allow_impression === false
      ? []
      : [{ id: "impression", label: t("product.impression"), icon: Layers }]),
    { id: "aucune", label: t("product.aucune"), icon: Ban },
  ];

  const addToCart = () => {
    const hasPerso = inscType !== "aucune" || !!inscText.trim() || !!logo;
    const effType = hasPerso ? (inscType === "aucune" ? (product.default_inscription || "impression") : inscType) : "aucune";
    add({
      product_id: product.id, name: product.name, name_en: product.name_en || "", image: displayImage || product.image,
      size_id: sizeId, size_label: product.sizes.find((s) => s.id === sizeId).label,
      size_label_en: product.sizes.find((s) => s.id === sizeId).label_en || "",
      finish_id: finishId || "",
      finish_label: (product.finishes || []).find((f) => f.id === finishId)?.label || "",
      finish_label_en: (product.finishes || []).find((f) => f.id === finishId)?.label_en || "",
      qty, inscription_type: effType, inscription_text: hasPerso ? inscText.trim() : "",
      color,
      logo_path: hasPerso ? logo?.path || "" : "", logo_name: hasPerso ? logo?.name || "" : "",
      logo_url: hasPerso ? logo?.url || "" : "",
      pricing: { base_price: product.base_price, sizes: product.sizes, tiers: product.tiers, inscription_fees: product.inscription_fees, price_tiers: product.price_tiers || [], finishes: product.finishes || [] },
    });
    toast.success(t("product.added_toast", { name: lp(product, lang).name }));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/85 backdrop-blur-sm"
      data-testid="product-popup" role="dialog" aria-modal="true"
      aria-label={product ? lp(product, lang).name : "Produit"} onClick={onClose}>
      <div data-lenis-prevent className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto overscroll-contain bg-surface border border-gold/25 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="popup-close"
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-ink/60 border border-ivory/15 text-ivory/70 hover:text-gold hover:border-gold/50 transition-colors duration-300">
          <X className="w-4 h-4" />
        </button>
        {!product ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>
        ) : (
          <div className="grid md:grid-cols-2">
            <div className="relative bg-[#f4f2ec] md:rounded-l-2xl overflow-hidden flex flex-col min-h-[320px]">
              <div className="relative flex-1 flex items-center justify-center">
                {displayImage || product.image ? (
                  <>
                    <img src={displayImage || product.image} alt={productAlt(product, lang)} data-testid="popup-image"
                      className="w-full h-full max-h-[560px] object-contain" />
                    <button type="button" data-testid="popup-preview-open" onClick={() => setPreviewOpen(true)}
                      title="Agrandir la photo"
                      className="absolute top-3 right-3 w-10 h-10 rounded-full bg-ink/80 text-gold border border-gold/40 flex items-center justify-center hover:bg-gold hover:text-ink transition-colors duration-300">
                      <Eye className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <p className="text-ink/30 text-sm" data-testid="popup-image-empty">{t("product.image_pending")}</p>
                )}
              </div>
              {/* Vignettes : photo de l'item + photos supplémentaires (ex. photo de groupe) */}
              {gallery.length > 1 && !curFinish?.image && !colorImg && (
                <div className="flex gap-2 p-3 overflow-x-auto justify-center border-t border-ink/5" data-testid="popup-gallery">
                  {gallery.map((src, gi) => (
                    <button key={src} type="button" onClick={() => setSelImg(src)}
                      data-testid={`popup-gallery-thumb-${gi}`}
                      aria-label={`${lp(product, lang).name} — photo ${gi + 1}`}
                      className={`shrink-0 w-16 h-16 rounded-lg bg-white p-1 border-2 transition-colors duration-300 ${(selImg || product.image) === src ? "border-gold" : "border-ink/10 hover:border-gold/50"}`}>
                      <img src={src} alt={`${productAlt(product, lang)} — photo ${gi + 1}`} loading="lazy" className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 md:p-8">
              <p className="text-gold tracking-[0.25em] uppercase text-[11px] mb-1.5">{lp(product, lang).category}</p>
              <h3 className="font-serif text-2xl mb-1" data-testid="popup-name">{lp(product, lang).name}</h3>
              {product.sku && <p className="text-xs text-ivory/65 mb-4" data-testid="popup-ref">Réf. {product.sku}</p>}

              {product.sizes.length > 1 && (
                <div className="mb-4">
                  <p className="text-xs text-ivory/60 mb-2">{t("product.size_title")}</p>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((s) => (
                      <button key={s.id} onClick={() => setSizeId(s.id)} data-testid={`popup-size-${s.id}`}
                        className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors duration-300 ${sizeId === s.id ? "border-gold bg-gold text-ink font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/50"}`}>
                        {lp(product, lang).sizeLabel(s)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {colorList.length > 1 && (
                <div className="mb-4">
                  <p className="text-xs text-ivory/60 mb-2">{t("product.color_title")}</p>
                  <select value={color} onChange={(e) => setColor(e.target.value)}
                    data-testid="popup-color-select"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm text-ivory focus:border-gold focus:outline-none [color-scheme:dark]">
                    {colorList.map((c) => (
                      <option key={c} value={c} data-testid={`popup-color-${c.toLowerCase()}`}>{c}</option>
                    ))}
                  </select>
                </div>
              )}

              {(product.finishes?.length || 0) > 1 && (                <div className="mb-4">
                  <p className="text-xs text-ivory/60 mb-2">{(lang === "en" ? product.finish_title_en : product.finish_title) || t("product.finish_title")}</p>
                  {product.finishes.length > 5 ? (
                    <select value={finishId || ""} onChange={(e) => setFinishId(e.target.value)}
                      data-testid="popup-finish-select"
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm text-ivory focus:border-gold focus:outline-none">
                      {product.finishes.map((f) => (
                        <option key={f.id} value={f.id} data-testid={`popup-finish-option-${f.id}`}>
                          {(lang === "en" && f.label_en ? f.label_en : f.label)}{f.extra > 0 ? ` +${fmt(f.extra)}` : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex flex-wrap gap-2" data-testid="popup-finish-selector">
                      {product.finishes.map((f) => (
                        <button key={f.id} onClick={() => setFinishId(f.id)} data-testid={`popup-finish-${f.id}`}
                          className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors duration-300 ${finishId === f.id ? "border-gold bg-gold text-ink font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/50"}`}>
                          {lang === "en" && f.label_en ? f.label_en : f.label}
                          {f.extra > 0 && <span className="ml-1 opacity-70">+{fmt(f.extra)}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4">
                <p className="text-xs text-ivory/60 mb-2">{t("product.qty_title")}</p>
                <div className="flex items-center gap-3">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="popup-qty-minus"
                    className="w-9 h-9 rounded-full border border-ivory/20 flex items-center justify-center hover:border-gold transition-colors duration-300">
                    <Minus className="w-4 h-4" />
                  </button>
                  <input type="number" min="1" value={qty} data-testid="popup-qty-input"
                    onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-center text-sm focus:border-gold focus:outline-none" />
                  <button onClick={() => setQty(qty + 1)} data-testid="popup-qty-plus"
                    className="w-9 h-9 rounded-full border border-ivory/20 flex items-center justify-center hover:border-gold transition-colors duration-300">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {(product.tiers?.length > 0 || product.price_tiers?.length > 0) && (
                  <div className="flex flex-wrap gap-2 mt-2.5" data-testid="popup-tiers">
                    {(product.price_tiers?.length
                      ? product.price_tiers.map((tr) => ({ key: tr.min_qty, label: `${tr.min_qty}+ → ${fmt(tr.price)}` }))
                      : product.tiers.map((tr) => ({ key: tr.min_qty, label: `${tr.min_qty}+ → −${Math.round(tr.discount * 100)} %` }))
                    ).map((b) => (
                      <span key={b.key} className={`text-[11px] px-2.5 py-1 rounded-full border ${qty >= b.key ? "border-gold text-gold" : "border-ivory/15 text-ivory/65"}`}>{b.label}</span>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <p className="text-xs text-ivory/60 mb-2">{t("product.insc_title")}</p>
                <div className="grid grid-cols-3 gap-2" data-testid="popup-inscription-selector">
                  {INSCRIPTION_TYPES.map((ty) => (
                    <button key={ty.id} onClick={() => setInscType(ty.id)} data-testid={`popup-inscription-${ty.id}`}
                      className={`p-2.5 rounded-lg border text-center transition-colors duration-300 ${inscType === ty.id ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}>
                      <ty.icon className={`w-4 h-4 mx-auto mb-1 ${inscType === ty.id ? "text-gold" : "text-ivory/70"}`} />
                      <p className="text-[11px] leading-tight">{ty.label}</p>
                      {/* Prix de personnalisation non affichés (décision du propriétaire
                          2026-10-09) : « à déterminer » — confirmés dans la soumission */}
                      {ty.id !== "aucune" && <p className="text-[10px] text-champagne mt-0.5">{t("product.fee_tbd")}</p>}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gold/80 mt-2" data-testid="popup-matrice-fee">{t("product.matrice_fee")}</p>
                {/* Personnalisation toujours offerte : texte (3 lignes min, grandit avec Entrée) + logo */}
                <PersoTextarea
                  value={inscText}
                  maxLength={200}
                  data-testid="popup-inscription-text"
                  onChange={onTextChange}
                  placeholder={ts("product.text_placeholder")}
                  className="w-full mt-3 bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none overflow-hidden"
                />
                <p className="text-[11px] text-ivory/60 mt-1.5" data-testid="popup-inscription-lines-hint">
                  {t("product.text_lines_hint")}
                </p>
                {product?.max_lines > 0 && (
                  <p data-testid="popup-inscription-max-lines" className="text-[11px] text-gold mt-1.5">
                    {t("product.text_lines_max", { n: product.max_lines })}
                  </p>
                )}
                <div className="mt-3">
                  <p className="text-[11px] text-ivory/70 mb-1.5">{t("product.logo_title")}</p>
                  <input ref={fileInput} type="file" accept=".png,.jpg,.jpeg,.svg,.pdf,.webp" className="hidden"
                    data-testid="popup-logo-input"
                    onChange={(e) => uploadLogo(e, () => { if (inscType === "aucune") setInscType(product?.default_inscription || "impression"); })} />
                  {logo ? (
                    <div className="flex items-center gap-2.5 bg-ink border border-gold/30 rounded-lg px-3 py-2" data-testid="popup-logo-chip">
                      {logo.name.toLowerCase().endsWith(".pdf")
                        ? <FileImage className="w-5 h-5 text-gold shrink-0" />
                        : <img src={`${API_BASE}${logo.url}`} alt="" className="h-7 w-7 object-contain rounded shrink-0" />}
                      <span className="text-xs text-ivory/80 truncate flex-1">{logo.name}</span>
                      <button type="button" data-testid="popup-logo-remove" onClick={() => setLogo(null)}
                        className="text-ivory/65 hover:text-red-400 transition-colors duration-300">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button type="button" data-testid="popup-logo-upload" onClick={() => fileInput.current?.click()}
                      disabled={uploading}
                      className="w-full flex items-center justify-center gap-2 border border-dashed border-ivory/25 rounded-lg px-3 py-2.5 text-xs text-ivory/60 hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-60">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                      {uploading ? t("product.logo_uploading") : t("product.logo_cta")}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-end justify-between border-t border-ivory/10 pt-4 mb-4">
                <div>
                  <p className="text-xs text-ivory/70">{t("product.unit_price")}</p>
                  <p className="font-serif text-xl text-champagne" data-testid="popup-unit-price">{fmt(unit)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-ivory/70">{t("product.total", { qty })}</p>
                  <p className="font-serif text-2xl text-gold" data-testid="popup-total">{fmt(unit * qty)}</p>
                </div>
              </div>

              <button onClick={addToCart} data-testid="popup-add-to-cart"
                className="w-full bg-gold text-ink font-semibold py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 mb-3">
                {t("product.add_to_cart")}
              </button>
              <Link to={`/produit/${product.id}`} onClick={onClose} data-testid="popup-view-full"
                className="block text-center text-xs text-ivory/70 hover:text-gold transition-colors duration-300 mb-3">
                {t("catalogue.view_full")}
              </Link>
              <div data-testid="popup-share" className="flex justify-center">
                <ShareButtons title={lp(product, lang).name} label={t("footer.share_page")}
                  url={`${window.location.origin}/produit/${product.id}`} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Visionneuse plein écran par-dessus la fiche — les clics ne doivent pas se
          propager au fond de la fiche (qui la fermerait avec la visionneuse) */}
      {previewOpen && (displayImage || product?.image) && (
        <div onClick={(e) => e.stopPropagation()}>
          <ImageLightbox src={displayImage || product.image} alt={productAlt(product, lang)}
            onClose={() => setPreviewOpen(false)} testid="popup-preview"
            labels={{ zoomIn: t("product.zoom_in"), zoomOut: t("product.zoom_out"), reset: t("product.zoom_reset"), close: t("product.zoom_close") }} />
        </div>
      )}
    </div>
  );
}
